import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, imgSrc } from "@/lib/api";
import { useT } from "@/lib/i18n";
import { useCurrency } from "@/lib/currency";
import Navbar from "@/components/layout/Navbar";
import Footer from "@/components/layout/Footer";
import { ArrowUpRight } from "lucide-react";

export default function Cabins() {
    const { t } = useT();
    const { format } = useCurrency();
    const [cabins, setCabins] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        api.get("/cabins").then((r) => setCabins(r.data)).finally(() => setLoading(false));
    }, []);

    return (
        <div data-testid="cabins-page" className="bg-bone min-h-screen">
            <Navbar />
            <div className="pt-32 lg:pt-40 pb-16 px-6 lg:px-12">
                <div className="max-w-[1500px] mx-auto">
                    <div className="grid grid-cols-1 md:grid-cols-12 gap-8 mb-16">
                        <div className="md:col-span-7">
                            <div className="eyebrow mb-4">{t("cabinsPage.eyebrow")}</div>
                            <h1 className="font-chiller text-5xl lg:text-7xl leading-[1.02] tracking-tight">
                                <span className="text-terracotta">Everyone's</span> <span className="text-gold">going to</span> <span className="text-forest">love this</span>
                            </h1>
                        </div>
                        <div className="md:col-span-4 md:col-start-9 self-end">
                            <p className="text-base text-muted2 leading-relaxed">
                                {t("cabinsPage.sub")}
                            </p>
                        </div>
                    </div>

                    {/* Our rooms — shared amenity + stay info panel */}
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 mt-4 mb-8 border-t border-espresso/15 pt-12" data-testid="cabins-our-rooms">
                        <div className="lg:col-span-7">
                            <div className="eyebrow mb-3">{t("cabinsPage.ourRoomsTitle")}</div>
                            <p className="text-base text-muted2 leading-relaxed max-w-2xl">
                                {t("cabinsPage.ourRoomsBody")}
                            </p>
                        </div>
                        <div className="lg:col-span-4 lg:col-start-9 self-center">
                            <div className="border-l-2 border-terracotta pl-5">
                                <div className="eyebrow text-terracotta mb-2" data-testid="cabins-adults-only">{t("cabinsPage.stayInfoTitle")}</div>
                                <div className="font-serif text-2xl leading-snug text-ink" data-testid="cabins-checkin">
                                    {t("cabinsPage.stayCheckIn")}
                                </div>
                                <div className="font-serif text-2xl leading-snug text-ink" data-testid="cabins-checkout">
                                    {t("cabinsPage.stayCheckOut")}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div className="px-6 lg:px-12 pb-24">
                <div className="max-w-[1500px] mx-auto">
                    {loading ? (
                        <div className="font-serif text-2xl text-espresso">Loading cabins…</div>
                    ) : (
                        <div className="space-y-32">
                            {cabins.map((c, i) => (
                                <article key={c.id} className={`grid grid-cols-1 lg:grid-cols-12 gap-10 items-center ${i % 2 === 1 ? "lg:[direction:rtl]" : ""}`} data-testid={`cabin-row-${i}`}>
                                    <div className="lg:col-span-7 lg:[direction:ltr]">
                                        <Link to={`/cabins/${c.slug}`} className="card-hover block">
                                            <div className="aspect-[16/10] overflow-hidden bg-bone2">
                                                <img src={imgSrc(c.images?.[0])} alt={c.name} className="card-img w-full h-full object-cover" />
                                            </div>
                                        </Link>
                                    </div>
                                    <div className="lg:col-span-4 lg:[direction:ltr] lg:col-start-9">
                                        <div className="eyebrow mb-3">Cabin No. {String(i + 1).padStart(2, "0")}</div>
                                        <h2 className="font-chiller text-4xl lg:text-5xl leading-tight tracking-tight mb-4">{c.name}</h2>
                                        <p className="text-base text-muted2 leading-relaxed mb-6">{c.tagline}</p>
                                        <div className="flex items-center gap-6 text-xs uppercase tracking-widest2 text-espresso mb-8">
                                            <span>{c.max_guests} guests</span>
                                            <span>{c.bedrooms} bed</span>
                                            <span>{c.size_sqft} sqft</span>
                                        </div>
                                        <div className="flex items-end justify-between">
                                            <div>
                                                <div className="eyebrow text-espresso/70">{t("common.from")}</div>
                                                <div className="font-serif text-3xl">{format(c.price_per_night)}<span className="text-base text-muted2"> {t("common.perNight")}</span></div>
                                            </div>
                                            <Link to={`/cabins/${c.slug}`} data-testid={`cabin-cta-${i}`} className="inline-flex items-center gap-2 text-xs uppercase tracking-widest2 font-semibold text-forest hover:text-terracotta">
                                                {t("cabinsPage.viewCabin")} <ArrowUpRight size={16} />
                                            </Link>
                                        </div>
                                    </div>
                                </article>
                            ))}
                        </div>
                    )}
                </div>
            </div>

            <Footer />
        </div>
    );
}
